/**
 * Markdown on the page: `markdown(source)` renders it formatted where it is read, and
 * <plane-markdown-input> is the text area where it is written, with a Write and a Preview tab.
 *
 *     <div class="markdown">${markdown(persona.soul)}</div>
 *
 *     <label for="persona-soul">Personality</label>
 *     <plane-markdown-input input-id="persona-soul" rows="8" .value=${soul} @input=${(event) => (soul = event.target.value)}></plane-markdown-input>
 *
 * The input renders in the light DOM of the page, so that the page's label points at its text area
 * and the page's styles (formStyles, markdownStyles) apply to it; the `input` events of the text
 * area reach the page as they are. The source is read by app/markdown.js: no HTML is rendered.
 */
import { LitElement, html } from 'lit';

import { parseMarkdown } from '../markdown.js';

/** The source formatted: its blocks, as Lit templates. */
export function markdown(source) {
  return renderBlocks(parseMarkdown(source));
}

function renderBlocks(blocks) {
  return blocks.map(renderBlock);
}

function renderBlock(block) {
  switch (block.type) {
    case 'heading':
      return renderHeading(block.level, renderInline(block.inline));
    case 'paragraph':
      return html`<p>${renderInline(block.inline)}</p>`;
    case 'code':
      return html`<pre><code>${block.text}</code></pre>`;
    case 'quote':
      return html`<blockquote>${renderBlocks(block.blocks)}</blockquote>`;
    case 'rule':
      return html`<hr />`;
    case 'list': {
      const items = block.items.map((item) => html`<li>${renderBlocks(item)}</li>`);
      return block.ordered ? html`<ol start=${block.start}>${items}</ol>` : html`<ul>${items}</ul>`;
    }
    default:
      return null;
  }
}

// Lit binds no tag names: one template per level
function renderHeading(level, content) {
  switch (level) {
    case 1:
      return html`<h1>${content}</h1>`;
    case 2:
      return html`<h2>${content}</h2>`;
    case 3:
      return html`<h3>${content}</h3>`;
    case 4:
      return html`<h4>${content}</h4>`;
    case 5:
      return html`<h5>${content}</h5>`;
    default:
      return html`<h6>${content}</h6>`;
  }
}

function renderInline(nodes) {
  return nodes.map((node) => {
    switch (node.type) {
      case 'text':
        return node.text;
      case 'break':
        return html`<br />`;
      case 'code':
        return html`<code>${node.text}</code>`;
      case 'strong':
        return html`<strong>${renderInline(node.children)}</strong>`;
      case 'em':
        return html`<em>${renderInline(node.children)}</em>`;
      case 'del':
        return html`<del>${renderInline(node.children)}</del>`;
      case 'link':
        return html`<a href=${node.href} target="_blank" rel="noopener noreferrer">${renderInline(node.children)}</a>`;
      default:
        return null;
    }
  });
}

class PlaneMarkdownInput extends LitElement {
  static properties = {
    value: { type: String },
    inputId: { type: String, attribute: 'input-id' },
    rows: { type: Number },
    placeholder: { type: String },
    disabled: { type: Boolean },
    // the class of the text area: "prose" on the customer pages (customerStyles)
    inputClass: { type: String, attribute: 'input-class' },
    preview: { state: true },
  };

  constructor() {
    super();
    this.value = '';
    this.inputId = '';
    this.rows = 8;
    this.placeholder = '';
    this.disabled = false;
    this.inputClass = '';
    this.preview = false;
    this.previewHeight = 0;
  }

  createRenderRoot() {
    return this;
  }

  /** Write or Preview; the preview as tall as the text area was, so that the page does not jump. */
  show(preview) {
    if (preview === this.preview) {
      return;
    }
    if (preview) {
      this.previewHeight = this.querySelector('textarea')?.offsetHeight ?? 0;
    }
    this.preview = preview;
  }

  render() {
    return html`
      <div class="markdown-tabs" role="tablist">
        <button type="button" role="tab" aria-selected=${String(!this.preview)} @click=${() => this.show(false)}>Write</button>
        <button type="button" role="tab" aria-selected=${String(this.preview)} @click=${() => this.show(true)}>Preview</button>
      </div>
      <textarea
        id=${this.inputId}
        class=${this.inputClass}
        rows=${this.rows}
        placeholder=${this.placeholder}
        ?disabled=${this.disabled}
        ?hidden=${this.preview}
        .value=${this.value ?? ''}
      ></textarea>
      ${this.preview
        ? html`
            <div class="markdown markdown-preview" role="tabpanel" style=${this.previewHeight ? `min-height: ${this.previewHeight}px` : ''}>
              ${this.value?.trim() ? markdown(this.value) : html`<p class="muted">Nothing to preview.</p>`}
            </div>
          `
        : null}
    `;
  }
}

customElements.define('plane-markdown-input', PlaneMarkdownInput);
